(() => {
  const TAG = 'button-price-card';
  if (customElements.get(TAG)) return;

  class ButtonPriceCard extends HTMLElement {
    constructor() {
      super();

      this.wrapper = null;
      this.compareElement = null;
      this.currentElement = null;
      this.root = null;
      this.idInput = null;

      this.sectionId = this.dataset.sectionId;
      this.productId = this.dataset.productId;
      this.variantId = this.dataset.variantId;
      this.variantPrice = parseInt(this.dataset.variantPrice) || 0;
      this.comparePrice = parseInt(this.dataset.comparePrice) || 0;
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.currencyCodeEnabled = this.dataset.currencyCodeEnabled === 'true';
      this.debug = this.dataset.debug === 'true' || window.ONHOW_DEBUG === true;

      this._unsub = null;
      this._observer = null;
      this._radioHandlers = [];
      this._idInputHandler = null;
    }

    log(...args) { if (this.debug) console.log('[OnHOW ButtonPriceCard]', ...args); }
    warn(...args) { if (this.debug) console.warn('[OnHOW ButtonPriceCard]', ...args); }
    err(...args) { console.error('[OnHOW ButtonPriceCard]', ...args); }

    connectedCallback() {
      this.wrapper = this.querySelector('.onhow-button-price__wrapper');
      this.compareElement = this.querySelector('.onhow-button-price__compare');
      this.currentElement = this.querySelector('.onhow-button-price__current');

      const roots = [
        this.closest('product-info'),
        this.closest('[id^="ProductInfo-"]'),
        this.closest('product-form'),
        this.closest('[data-product-id]'),
        this.closest('.card__content'),
        document
      ].filter(Boolean);
      this.root = roots[0];

      this.log('connected', {
        sectionId: this.sectionId,
        productId: this.productId,
        root: this.root && this.root.tagName ? this.root.tagName.toLowerCase() : 'document'
      });

      const radios = this.root.querySelectorAll(
        'variant-selects input[type="radio"], input[type="radio"][name*="Choose"], .product-form__input input[type="radio"]'
      );
      radios.forEach(r => {
        const h = () => { this.log('radio change', { name: r.name, value: r.value }); this.updateFromDOM('radio'); };
        r.addEventListener('change', h);
        this._radioHandlers.push({ el: r, h });
      });
      this.log('radio listeners attached', radios.length);

      this.idInput = this.root.querySelector('.product-variant-id');
      if (this.idInput) {
        const h = () => { this.log('hidden variant id change', this.idInput.value); this.updateFromDOM('id-input'); };
        this.idInput.addEventListener('change', h);
        this._idInputHandler = { el: this.idInput, h };

        this._observer = new MutationObserver(() => {
          this.log('mutation observed on .product-variant-id', this.idInput.value);
          this.updateFromDOM('mutation');
        });
        this._observer.observe(this.idInput, { attributes: true, attributeFilter: ['value'] });
      } else {
        this.warn('no .product-variant-id found under root');
      }

      try {
        const topic = (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) || 'variant:change';
        if (typeof window.subscribe === 'function') {
          this._unsub = subscribe(topic, (e) => {
            this.log('pubsub received', e);
            this.handleVariantChangeFromPubSub(e);
          });
          this.log('subscribed to', topic);
        } else {
          this.log('subscribe() not found; skipping pubsub');
        }
      } catch (e) {
        this.warn('pubsub subscribe error', e);
      }

      this.updateFromDOM('init');
    }

    disconnectedCallback() {
      if (this._unsub) { try { this._unsub(); } catch (_) {} this._unsub = null; }
      if (this._observer) { try { this._observer.disconnect(); } catch (_) {} this._observer = null; }
      if (this._idInputHandler) {
        const { el, h } = this._idInputHandler;
        el.removeEventListener('change', h);
        this._idInputHandler = null;
      }
      if (this._radioHandlers) {
        this._radioHandlers.forEach(({ el, h }) => el.removeEventListener('change', h));
        this._radioHandlers = [];
      }
      this.log('disconnected');
    }

    getHandle() {
      const vs = this.root.querySelector('variant-selects');
      let url = vs?.dataset?.url || '';

      if (!url) {
        const link = this.root.querySelector('a.full-unstyled-link, .card__heading a');
        url = link?.getAttribute('href') || '';
      }
      if (!url) return null;

      const m = url.match(/\/products\/([^?]+)/);
      const handle = m ? m[1] : url;
      return handle.replace(/^\/|\/$/g, '') || null;
    }

    lookupVariantById(variantId) {
      const handle = this.getHandle();
      const map = (window.cart_items && handle) ? window.cart_items[handle] : null;
      if (Array.isArray(map)) {
        const v = map.find(x => String(x.id) === String(variantId));
        if (v) return v;
      }
      return null;
    }

    readSelectedVariantFromScript() {
      const script = this.root.querySelector('script[data-selected-variant]');
      if (!script) return null;
      try {
        const json = script.textContent.trim();
        return json ? JSON.parse(json) : null;
      } catch (e) {
        this.warn('failed to parse <script data-selected-variant>', e);
        return null;
      }
    }

    updateFromDOM(source) {
      let variantId = null;

      if (this.idInput && this.idInput.value) {
        variantId = parseInt(this.idInput.value, 10);
      }

      this.log('updateFromDOM start', { source, variantId });

      let variantObj = null;

      if (variantId) {
        variantObj = this.lookupVariantById(variantId);
        if (!variantObj) {
          this.warn('variantId present but not found in window.cart_items', {
            variantId,
            handle: this.getHandle(),
            cart_items_keys: window.cart_items ? Object.keys(window.cart_items) : 'no cart_items'
          });
        }
      }

      if (!variantObj) {
        const vScript = this.readSelectedVariantFromScript();
        if (vScript) {
          variantObj = vScript;
          variantId = vScript.id || variantId;
          this.log('fallback: using script[data-selected-variant]', { variantId });
        }
      }

      if (!variantObj) {
        this.warn('no variant resolved; falling back to dataset values', {
          datasetPrice: this.variantPrice,
          datasetCompare: this.comparePrice
        });
        this.updatePrice();
        return;
      }

      this.variantId = variantId || this.variantId;
      this.variantPrice = typeof variantObj.price === 'number' ? variantObj.price : this.variantPrice;
      this.comparePrice = (typeof variantObj.compare_at_price === 'number' && variantObj.compare_at_price) ? variantObj.compare_at_price : 0;

      this.log('resolved variant', {
        variantId: this.variantId,
        price: this.variantPrice,
        compare_at_price: this.comparePrice
      });

      this.updatePrice();
    }

    handleVariantChangeFromPubSub(event) {
      const variant = event?.data?.variant || event?.variant || null;
      if (!variant) { this.warn('pubsub payload missing variant'); return; }

      const eventProductId = String(variant.product_id || variant.productId || '');
      const myProductId = String(this.productId || '');

      if (myProductId && eventProductId && eventProductId !== myProductId) {
        this.log('pubsub ignored (different product)', { eventProductId, myProductId });
        return;
      }

      this.variantId = variant.id || this.variantId;
      this.variantPrice = (typeof variant.price === 'number') ? variant.price : this.variantPrice;
      this.comparePrice = (typeof variant.compare_at_price === 'number') ? variant.compare_at_price : 0;

      this.log('pubsub applied', {
        variantId: this.variantId,
        price: this.variantPrice,
        compare_at_price: this.comparePrice
      });

      this.updatePrice();
    }

    updatePrice() {
      const quantity = 1;
      const totalPrice = Math.max(0, (Number(this.variantPrice) || 0) * quantity);
      const totalCompare = Math.max(0, (Number(this.comparePrice) || 0) * quantity);

      const formatted = this.formatMoney(totalPrice);
      const formattedCompare = totalCompare > totalPrice ? this.formatMoney(totalCompare) : null;

      this.log('render', { totalPrice, totalCompare, formatted, formattedCompare });

      if (this.currentElement) this.currentElement.textContent = formatted;

      if (formattedCompare) {
        if (!this.compareElement) {
          this.compareElement = document.createElement('s');
          this.compareElement.className = 'onhow-button-price__compare';
          if (this.wrapper && this.currentElement) {
            this.wrapper.insertBefore(this.compareElement, this.currentElement);
          }
        }
        this.compareElement.textContent = formattedCompare;
        this.compareElement.style.display = '';
      } else if (this.compareElement) {
        this.compareElement.style.display = 'none';
      }
    }

    formatMoney(cents) {
      try {
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(cents, this.moneyFormat);
        }
      } catch (e) {
        this.warn('Shopify.formatMoney error', e);
      }
      const dollars = (Number(cents) / 100).toFixed(2);
      return (this.moneyFormat || '${{amount}}').replace('{{amount}}', dollars);
    }
  }

  customElements.define(TAG, ButtonPriceCard);
})();